import { CopyVariant, Visual } from '../../consultantTypes';
import { FileText, Image, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

interface ComposerSidebarProps {
  copyVariants: CopyVariant[];
  visuals: Visual[];
  canShare: boolean;
}

export function ComposerSidebar({ copyVariants, visuals, canShare }: ComposerSidebarProps) {
  const selectedCount = copyVariants.filter(c => c.selected).length;
  const approvedVisuals = visuals.filter(v => v.status === 'APPROVED').length;
  const pendingVisuals = visuals.filter(v => v.status === 'UPLOADED').length;

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 sticky top-24">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Samenvatting</h3>

      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Selected Copy</span>
            </div>
            <span className={`text-lg font-bold ${
              selectedCount >= 3 && selectedCount <= 4 ? 'text-green-600' : 'text-gray-600'
            }`}>
              {selectedCount}/4
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                selectedCount >= 3 && selectedCount <= 4 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${(selectedCount / 4) * 100}%` }}
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Image className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Visuals</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-900">Approved</span>
              </div>
              <span className="text-sm font-bold text-green-700">{approvedVisuals}</span>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-900">Pending</span>
              </div>
              <span className="text-sm font-bold text-gray-700">{pendingVisuals}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        {canShare ? (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-900">Klaar om te delen</p>
              <p className="text-xs text-green-700 mt-1">
                Alle criteria zijn voldaan
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Nog niet klaar</p>
              <ul className="text-xs text-gray-600 mt-1 space-y-1">
                {(selectedCount < 3 || selectedCount > 4) && (
                  <li>• Selecteer 3-4 teksten</li>
                )}
                {approvedVisuals < 3 && (
                  <li>• Approve minimaal 3 visuals</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
